import clsx from "clsx";
import { Spinner } from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useTranslations } from "translations";
import { setupPdfWorker } from "./pdfWorker";

setupPdfWorker();

export type PdfViewerFile = {
  fileName: string;
  url: string;
  uploadedAt?: number;
  id: string | number;
};

const useContainerWidth = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    setContainerWidth(containerRef.current.clientWidth);
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let frameId = 0;
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => setContainerWidth(width));
    });

    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    containerWidth,
  };
};

export const PdfViewer = ({
  isLoadingPdf,
  selectedFile,
}: {
  selectedFile: PdfViewerFile;
  isLoadingPdf?: boolean;
}) => {
  const translations = useTranslations();
  const toast = useToast();

  const [numPages, setNumPages] = useState<number>(0);
  const { containerRef, containerWidth } = useContainerWidth();

  const memoizedFile = useMemo(
    () => ({
      url: selectedFile.url,
    }),
    [selectedFile],
  );

  const handleError = (err: Error) => {
    console.error("PDF load error:", err.message);
    toast({ color: "danger", message: translations.general.error });
  };

  if (!memoizedFile.url) return null;

  return (
    <div
      className={clsx(
        "flex h-[calc(95vh-140px)] w-full overflow-y-auto shadow-xl",
        {
          "items-center justify-center": isLoadingPdf,
        },
      )}
    >
      {isLoadingPdf ? (
        <Spinner
          color="primary"
          size="lg"
          testId="spinner-loading-pdf-document"
        />
      ) : (
        <div
          ref={containerRef}
          className="flex w-full justify-center rounded-[2px] border border-gray-400"
        >
          <Document
            file={memoizedFile}
            loading={
              <div className="flex h-full w-full items-center justify-center">
                <Spinner
                  color="primary"
                  size="lg"
                  testId="spinner-loading-pdf-document"
                />
              </div>
            }
            onLoadError={handleError}
            onLoadSuccess={(info) => setNumPages(info.numPages)}
            onSourceError={handleError}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                renderForms
                width={containerWidth}
              />
            ))}
          </Document>
        </div>
      )}
    </div>
  );
};
